import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Camera, Video, ShieldCheck, Sparkles, Award, CheckCircle2,
  FileText, Eye, HandCoins, Globe, KeyRound, Lock,
} from 'lucide-react';
import Seo from '../components/Seo';

const SellYourCar = () => {
  const { t } = useTranslation();

  const offers = [
    { icon: Camera, label: t('sell.offers.photos', 'Photos premium') },
    { icon: Video, label: t('sell.offers.video', 'Vidéo de présentation') },
    { icon: Eye, label: t('sell.offers.immersive', 'Captation immersive') },
    { icon: Sparkles, label: t('sell.offers.dayNight', 'Présentation jour & nuit') },
    { icon: Award, label: t('sell.offers.branding', 'Branding AutoDistrict') },
    { icon: ShieldCheck, label: t('sell.offers.warranty', 'Garantie 1 an incluse*') },
  ];

  const handles = [
    { icon: FileText, title: t('sell.handle.listing', "Création de l'annonce") },
    { icon: Globe, title: t('sell.handle.visibility', 'Visibilité en ligne') },
    { icon: Eye, title: t('sell.handle.requests', 'Demandes acheteurs') },
    { icon: CheckCircle2, title: t('sell.handle.viewings', 'Organisation des visites') },
    { icon: HandCoins, title: t('sell.handle.negotiation', 'Négociations') },
    { icon: ShieldCheck, title: t('sell.handle.closing', "Accompagnement jusqu'à la vente") },
  ];

  const criteria = [
    t('sell.criteria.history', 'Historique clair'),
    t('sell.criteria.maintenance', 'Entretien suivi'),
    t('sell.criteria.config', 'Configuration attractive'),
    t('sell.criteria.condition', 'État général cohérent'),
    t('sell.criteria.profile', 'Véhicule premium, passion ou bien entretenu'),
  ];

  return (
    <div className="bg-[#F7F8FA] pt-16 lg:pt-20" data-testid="sell-page">
      <Seo title={t('seo.sell.title')} description={t('seo.sell.desc')} />
      {/* HERO */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <span className="section-eyebrow">{t('sell.eyebrow', 'Vente accompagnée premium')}</span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('sell.heroTitle', 'Revendez votre voiture')}{' '}
              <span className="text-blue-900">{t('sell.heroTitleAccent', 'avec exigence.')}</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              {t('sell.heroSubtitle', 'Une sélection stricte. Une présentation professionnelle. Une vente sécurisée.')}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/estimer-mon-vehicule" className="btn-primary" data-testid="sell-cta-hero">
                {t('sell.cta.primary', 'Faites estimer votre véhicule')}
                <ArrowRight size={16} />
              </Link>
              <button
                type="button"
                onClick={() => document.getElementById('approach')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary"
                data-testid="sell-discover-btn"
              >
                {t('sell.cta.secondary', 'Découvrir le service')}
              </button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={16} className="text-emerald-500" /> {t('sell.trust1', 'Estimation gratuite')}</span>
              <span className="inline-flex items-center gap-1.5"><Lock size={16} className="text-emerald-500" /> {t('sell.trust2', 'Vente sécurisée')}</span>
              <span className="inline-flex items-center gap-1.5"><Award size={16} className="text-emerald-500" /> {t('sell.trust3', 'Standards premium')}</span>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-slate-200 shadow-md">
              <img
                src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=1400&q=80"
                alt="Vente de voiture accompagnée"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-16 sm:py-20" id="approach">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="section-eyebrow">{t('sell.intro.eyebrow', 'Notre approche')}</span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('sell.intro.title', "Nous ne publions pas n'importe quel véhicule.")}
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            {t('sell.intro.p1', "Contrairement aux plateformes traditionnelles entre particuliers, chaque véhicule proposé par AutoDistrict est analysé, présenté et accompagné selon nos standards de qualité. Nous accompagnons les propriétaires sélectionnés dans la vente de leur véhicule lorsqu'il correspond à nos critères.")}
          </p>
        </div>
      </section>

      {/* CRITERIA */}
      <section className="pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="section-eyebrow">{t('sell.criteria.eyebrow', 'Critères de sélection')}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('sell.criteria.title', 'Chaque véhicule est sélectionné individuellement.')}
            </h2>
            <p className="mt-3 text-slate-600">
              {t('sell.criteria.sub', "Une analyse préalable rigoureuse, parce que la qualité prime sur le volume.")}
            </p>
            <ul className="mt-6 space-y-3">
              {criteria.map((c, i) => (
                <li key={i} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
                  <span className="w-7 h-7 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 size={15} />
                  </span>
                  <span className="text-slate-800 text-sm">{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
            <img
              src="https://images.pexels.com/photos/11774110/pexels-photo-11774110.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Vintage classic car"
              className="w-full h-full object-cover grayscale"
            />
          </div>
        </div>
      </section>

      {/* PRESENTATION OFFERS */}
      <section className="py-16 sm:py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="section-eyebrow">{t('sell.presentation.eyebrow', 'Présentation premium')}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('sell.presentation.title', 'Une expérience proche des standards des concessions premium.')}
            </h2>
            <p className="mt-4 text-slate-600">
              {t('sell.presentation.sub', 'Chaque véhicule bénéficie d\'une présentation professionnelle qui révèle son véritable potentiel.')}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {offers.map((o, i) => {
              const Icon = o.icon;
              return (
                <div key={i} className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4 hover:bg-white hover:shadow-sm transition-all">
                  <span className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 text-blue-900 flex items-center justify-center flex-shrink-0">
                    <Icon size={18} />
                  </span>
                  <span className="text-slate-800 text-sm font-medium">{o.label}</span>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mt-6">
            {t('sell.warranty.disclaimer', '* Garantie applicable selon le véhicule et son éligibilité. Conditions confirmées au cas par cas.')}
          </p>
        </div>
      </section>

      {/* HANDLE */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('sell.handle.title', 'Nous gérons tout.')}
            </h2>
            <p className="mt-4 text-slate-600">
              {t('sell.handle.sub', 'Du premier contact à la poignée de main finale — vous vous concentrez sur le résultat, nous gérons le reste.')}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {handles.map((h, i) => {
              const Icon = h.icon;
              return (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-600 flex items-center justify-center mb-3">
                    <Icon size={18} />
                  </div>
                  <h3 className="text-slate-900 font-semibold text-sm">{h.title}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-gradient-to-br from-blue-900 to-blue-800 rounded-3xl overflow-hidden px-8 py-12 sm:px-12 sm:py-16 text-center">
            <span className="inline-block bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-5">
              {t('sell.finalCta.eyebrow', 'Prêt à commencer ?')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 max-w-3xl mx-auto" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('sell.finalCta.title', 'Faites estimer votre véhicule gratuitement.')}
            </h2>
            <p className="text-blue-100 max-w-xl mx-auto mb-8">
              {t('sell.finalCta.desc', 'Notre équipe revient vers vous sous 48h avec une analyse personnalisée.')}
            </p>
            <Link to="/estimer-mon-vehicule" className="inline-flex items-center justify-center gap-2 bg-white text-blue-900 hover:bg-blue-50 px-7 py-4 rounded-full font-semibold text-sm transition-colors" data-testid="sell-cta-bottom">
              {t('sell.cta.primary', 'Faites estimer votre véhicule')}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SellYourCar;
